// Celetus — QuantityPicker
// ------------------------------------------------------------
// Recursos:
// - MUI (@mui/material + @mui/icons-material)
// - Controlado (value + onChange)
// - Limites min/max e estoque (stock)
// - Step configurável
// - Teclado: ↑/↓, PgUp/PgDown, Home/End
// - Acessibilidade: labels, aria-live
// - Long-press no +/− com aceleração
// - input numérico mobile (inputMode="numeric", pattern)
// - Validação em blur + clamp
// - Estados disabled/out-of-stock
// - Sem dependências além do MUI
// ------------------------------------------------------------

import * as React from "react"
import { Box, IconButton, TextField, Tooltip } from "@mui/material"
import AddRounded from "@mui/icons-material/AddRounded"
import RemoveRounded from "@mui/icons-material/RemoveRounded"

export type QuantityPickerProps = {
  value: number
  onChange: (next: number) => void
  min?: number // default 1
  max?: number // limite lógico (por pedido)
  step?: number // default 1
  stock?: number // estoque disponível (aplica clamp ao menor entre max e stock)
  disabled?: boolean
  size?: "sm" | "md" | "lg" // controla dimensões via sx
  "aria-label"?: string
  sx?: any // forward p/ Box
}

function clamp(n: number, min: number, max: number) {
  return Math.min(Math.max(n, min), max)
}

function parseToIntSafe(v: string) {
  const onlyDigits = v.replace(/\D+/g, "")
  if (!onlyDigits) return NaN
  return parseInt(onlyDigits, 10)
}

export const QuantityPicker: React.FC<QuantityPickerProps> = ({
  value,
  onChange,
  min = 1,
  max,
  step = 1,
  stock,
  disabled,
  size = "md",
  sx,
  ...aria
}) => {
  const effectiveMax = React.useMemo(() => {
    const caps: number[] = []
    if (typeof max === "number") caps.push(max)
    if (typeof stock === "number") caps.push(stock)
    return caps.length ? Math.min(...caps) : Number.POSITIVE_INFINITY
  }, [max, stock])

  const [raw, setRaw] = React.useState<string>(String(value))
  const liveRef = React.useRef<HTMLDivElement | null>(null)

  React.useEffect(() => {
    setRaw(String(value))
  }, [value])

  const commit = React.useCallback(
    (next: number) => {
      const clamped = clamp(
        next,
        min,
        Number.isFinite(effectiveMax)
          ? (effectiveMax as number)
          : Number.MAX_SAFE_INTEGER
      )
      if (clamped !== value) onChange(clamped)
    },
    [effectiveMax, min, onChange, value]
  )

  const stepTo = React.useCallback(
    (dir: 1 | -1) => {
      if (disabled) return
      const next = value + dir * step
      commit(next)
    },
    [commit, disabled, step, value]
  )

  // Long-press + aceleração
  const pressTimer = React.useRef<{
    tid?: number
    start?: number
    dir?: 1 | -1
  } | null>(null)
  const handlePressStart = (dir: 1 | -1) => {
    if (disabled) return
    stepTo(dir)
    const t: any = { start: performance.now(), dir }
    const loop = () => {
      const elapsed = performance.now() - (t.start ?? 0)
      const speed = elapsed < 600 ? 300 : elapsed < 1200 ? 150 : 75
      stepTo(t.dir)
      t.tid = window.setTimeout(loop, speed) as unknown as number
    }
    t.tid = window.setTimeout(loop, 400) as unknown as number // delay antes de acelerar
    pressTimer.current = t
  }
  const handlePressEnd = () => {
    if (pressTimer.current?.tid) window.clearTimeout(pressTimer.current.tid)
    pressTimer.current = null
  }

  const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const nextRaw = e.target.value
    setRaw(nextRaw)
    const parsed = parseToIntSafe(nextRaw)
    if (!Number.isNaN(parsed)) {
      const clamped = clamp(
        parsed,
        min,
        Number.isFinite(effectiveMax)
          ? (effectiveMax as number)
          : Number.MAX_SAFE_INTEGER
      )
      if (clamped !== value) onChange(clamped)
    }
  }

  const onInputBlur = () => {
    const parsed = parseToIntSafe(raw)
    if (Number.isNaN(parsed)) {
      setRaw(String(value))
      if (liveRef.current)
        liveRef.current.textContent = `Quantidade mantida em ${value}`
      return
    }
    const clamped = clamp(
      parsed,
      min,
      Number.isFinite(effectiveMax)
        ? (effectiveMax as number)
        : Number.MAX_SAFE_INTEGER
    )
    if (clamped !== value) onChange(clamped)
    setRaw(String(clamped))
  }

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (disabled) return
    if (e.key === "ArrowUp") {
      e.preventDefault()
      stepTo(1)
    } else if (e.key === "ArrowDown") {
      e.preventDefault()
      stepTo(-1)
    } else if (e.key === "PageUp") {
      e.preventDefault()
      commit(value + step * 5)
    } else if (e.key === "PageDown") {
      e.preventDefault()
      commit(value - step * 5)
    } else if (e.key === "Home") {
      e.preventDefault()
      commit(min)
    } else if (e.key === "End") {
      e.preventDefault()
      commit(Number.isFinite(effectiveMax) ? (effectiveMax as number) : value)
    }
  }

  const dims = {
    sm: { btn: 36, fw: 56, tfSize: "small" as const },
    md: { btn: 40, fw: 64, tfSize: "small" as const },
    lg: { btn: 48, fw: 72, tfSize: "medium" as const },
  }[size]

  const outOfStock =
    (stock !== undefined && stock <= 0) ||
    (Number.isFinite(effectiveMax) && (effectiveMax as number) < min)

  return (
    <Box
      role="group"
      aria-disabled={disabled || outOfStock}
      sx={{
        display: "inline-flex",
        alignItems: "stretch",
        border: 1,
        borderColor: "divider",
        borderRadius: 2,
        overflow: "hidden",
        bgcolor: "background.paper",
        ...(sx || {}),
      }}
    >
      <Tooltip title="Diminuir" disableHoverListener={disabled}>
        <span>
          <IconButton
            aria-label="Diminuir quantidade"
            size={dims.tfSize}
            disabled={disabled || outOfStock || value <= min}
            onMouseDown={() => handlePressStart(-1)}
            onMouseUp={handlePressEnd}
            onMouseLeave={handlePressEnd}
            onTouchStart={() => handlePressStart(-1)}
            onTouchEnd={handlePressEnd}
            //onClick={() => stepTo(-1)}
            sx={{ width: dims.btn, height: dims.btn, borderRadius: 0 }}
          >
            <RemoveRounded />
          </IconButton>
        </span>
      </Tooltip>

      <TextField
        variant="outlined"
        size={dims.tfSize}
        value={raw}
        onChange={onInputChange}
        onBlur={onInputBlur}
        onKeyDown={onKeyDown}
        inputProps={{
          inputMode: "numeric",
          pattern: "[0-9]*",
          "aria-label": aria["aria-label"] ?? "Quantidade",
          style: { textAlign: "center", width: dims.fw },
        }}
        disabled={disabled || outOfStock}
        sx={{
          "& .MuiOutlinedInput-notchedOutline": { border: 0 },
          "& .MuiOutlinedInput-root": { borderRadius: 0 },
        }}
      />

      <Tooltip title="Aumentar" disableHoverListener={disabled}>
        <span>
          <IconButton
            aria-label="Aumentar quantidade"
            size={dims.tfSize}
            disabled={
              disabled ||
              outOfStock ||
              (Number.isFinite(effectiveMax) &&
                value >= (effectiveMax as number))
            }
            onMouseDown={() => handlePressStart(1)}
            onMouseUp={handlePressEnd}
            onMouseLeave={handlePressEnd}
            onTouchStart={() => handlePressStart(1)}
            onTouchEnd={handlePressEnd}
            //onClick={() => stepTo(1)}
            sx={{ width: dims.btn, height: dims.btn, borderRadius: 0 }}
          >
            <AddRounded />
          </IconButton>
        </span>
      </Tooltip>

      {/* Feedback acessível */}
      <div
        aria-live="polite"
        aria-atomic="true"
        style={{
          position: "absolute",
          width: 1,
          height: 1,
          overflow: "hidden",
          clip: "rect(1px, 1px, 1px, 1px)",
        }}
        ref={liveRef}
      />
    </Box>
  )
}
